import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';

export type ShareImageResult = 'shared' | 'copied' | 'downloaded' | 'failed';

async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the "data:image/png;base64," prefix — Filesystem.writeFile wants raw base64.
      resolve(result.substring(result.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function shareViaCapacitor(blob: Blob, filename: string): Promise<ShareImageResult> {
  try {
    const base64 = await blobToBase64(blob);
    const written = await Filesystem.writeFile({
      path: filename,
      data: base64,
      directory: Directory.Cache,
    });
    await Share.share({
      files: [written.uri],
      dialogTitle: 'Share Lavascend',
    });
    return 'shared';
  } catch (err) {
    console.warn('[share] Capacitor share failed:', err);
    return 'failed';
  }
}

export async function sharePayloadImage(
  blob: Blob,
  filename = 'lavascend.png',
): Promise<ShareImageResult> {
  // 0) In the Capacitor APK the WebView lacks navigator.share, so route to the
  // native Android Intent.ACTION_SEND via the @capacitor/share plugin.
  if (Capacitor.isNativePlatform()) {
    return shareViaCapacitor(blob, filename);
  }

  const file = new File([blob], filename, { type: blob.type });
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
    share?: (data: ShareData) => Promise<void>;
  };

  // 1) Native share with files (mobile-first; modern desktop browsers also support it).
  // Some browsers expose `share` without `canShare`; in that case skip the precheck
  // and let `share` itself decide — its rejection is our signal to fall back.
  const hasShare = typeof nav.share === 'function';
  const canShareFiles = nav.canShare ? nav.canShare({ files: [file] }) : true;
  if (hasShare && canShareFiles) {
    try {
      await nav.share({ files: [file] });
      return 'shared';
    } catch (err) {
      const name = (err as { name?: string }).name;
      // User-canceled is silent success.
      if (name === 'AbortError') return 'shared';
      // NotAllowedError typically means transient user activation expired
      // (too much async work happened between the tap and this call).
      console.warn('[share] navigator.share rejected, falling through:', name, err);
    }
  } else {
    console.warn('[share] native share unavailable', { hasShare, canShareFiles });
  }

  // 2) Clipboard image (desktop primary path).
  try {
    if (
      typeof ClipboardItem !== 'undefined' &&
      navigator.clipboard &&
      typeof navigator.clipboard.write === 'function'
    ) {
      await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
      return 'copied';
    }
  } catch (err) {
    console.warn('[share] clipboard.write rejected, falling through:', err);
  }

  // 3) Download fallback — last-resort, works everywhere.
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return 'downloaded';
  } catch {
    return 'failed';
  }
}
